export interface ProductSpec {
  label: string;
  value: string;
}

export interface ProductData {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  badge?: string;
  category: string;
  moq: string;
  leadTime: string;
  specs: ProductSpec[];
  materials: string[];
  materialNote?: string;
  customizationOptions: string[];
  useCases: string[];
  highlights: string[];
  relatedIds?: string[];
}

const allProducts: ProductData[] = [
  // ─── E-Commerce & Retail ───────────────────────────────────────────────────
  {
    id: 'ecom-envelopes',
    name: 'Ecom Envelopes',
    tagline: 'Kraft & bubble mailer envelopes built for safe, sustainable delivery.',
    description: 'Our ecom envelopes are engineered for the last-mile — lightweight, tear-resistant, and fully branded. Available in kraft paper and bubble-lined variants to suit fragile or flat shipments.',
    image: '/assets/images/Ecom_envelope-1787035235342.jpeg',
    alt: 'Brown kraft paper ecommerce mailing envelopes stacked together',
    badge: 'Bestseller',
    category: 'E-Commerce',
    moq: '500 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Available Sizes', value: 'A5, A4, A3, Custom' },
      { label: 'GSM Range', value: '80–120 GSM (kraft); 50 GSM + bubble lining' },
      { label: 'Closure', value: 'Self-seal peel & stick' },
      { label: 'Print Area', value: 'Full exterior surface' },
      { label: 'Printing', value: 'Flexo / Digital / Offset' },
    ],
    materials: ['Recycled kraft paper', 'Recycled bubble lining (bubble variant)', 'Water-based adhesive'],
    materialNote: 'All materials are 100% recyclable. No virgin plastic used.',
    customizationOptions: [
      'Custom size & dimensions',
      'Brand logo & full-colour print',
      'Custom interior print',
      'Tear strip or perforated opening',
      'QR code / barcode integration',
    ],
    useCases: ['D2C apparel brands', 'Beauty & skincare', 'Books & stationery', 'Electronics accessories'],
    highlights: ['Tear-resistant', 'Water-resistant outer', 'Tamper-evident seal', 'Lightweight — reduces shipping cost'],
    relatedIds: ['ecom-boxes', 'tapes', 'thank-you-cards'],
  },
  {
    id: 'ecom-boxes',
    name: 'Ecom Boxes',
    tagline: 'Corrugated & rigid boxes in every size — ready for your brand.',
    description: 'From mailer boxes to full corrugated shippers, our ecom boxes are designed to protect products and impress customers at unboxing. Fully customisable inside and out.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__12_10_47-1787035801448.jpeg',
    alt: 'Sustainable kraft cardboard boxes for product packaging',
    category: 'E-Commerce',
    moq: '200 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Box Types', value: 'Mailer box, RSC, Full-overlap, Rigid' },
      { label: 'Flute Options', value: 'B-flute, E-flute, F-flute' },
      { label: 'Board Thickness', value: '3-ply / 5-ply' },
      { label: 'Printing', value: 'Flexo, Digital, Litho-laminate' },
      { label: 'Finish', value: 'Kraft natural, White, Custom colour' },
    ],
    materials: ['Recycled corrugated board', 'Kraft liner', 'Water-based inks'],
    materialNote: 'Minimum 70% post-consumer recycled content.',
    customizationOptions: [
      'Custom dimensions (L × W × H)',
      'Interior print & tissue paper',
      'Magnetic closure upgrade',
      'Custom inserts & dividers',
      'Embossing / debossing',
    ],
    useCases: ['Subscription boxes', 'Gift packaging', 'Electronics', 'Food & beverage'],
    highlights: ['Crush-resistant', 'Stackable design', 'Easy self-assembly', 'Recyclable & compostable'],
    relatedIds: ['ecom-envelopes', 'honeycomb-paper', 'thank-you-cards'],
  },
  {
    id: 'tapes',
    name: 'Tapes',
    tagline: 'Kraft & custom-printed sealing tapes that seal the deal — literally.',
    description: 'Replace generic brown tape with a branded experience. Our kraft paper tapes and custom-printed variants turn every parcel into a brand touchpoint.',
    image: '/assets/images/Ecom_envelopes_1-1787035102140.jpeg',
    alt: 'Custom printed kraft paper sealing tape rolls',
    category: 'Packaging',
    moq: '500 rolls',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Width Options', value: '24mm, 36mm, 48mm, 72mm' },
      { label: 'Length', value: '50m / 100m per roll' },
      { label: 'Adhesive', value: 'Water-activated (gummed) or Pressure-sensitive' },
      { label: 'Print Colours', value: 'Up to 4 spot colours' },
      { label: 'Core Size', value: '76mm standard' },
    ],
    materials: ['Kraft paper backing', 'Natural rubber adhesive', 'Water-based inks'],
    materialNote: 'Kraft paper tapes are fully recyclable with the box — no need to remove before recycling.',
    customizationOptions: [
      'Logo & brand text print',
      'Custom width & length',
      'Coloured kraft base',
      'Eco messaging / sustainability claims',
    ],
    useCases: ['E-commerce fulfilment', 'Warehouse sealing', 'Gift wrapping', 'Retail packaging'],
    highlights: ['Recyclable with box', 'Strong adhesion', 'Tear-by-hand', 'Custom branding'],
    relatedIds: ['ecom-boxes', 'ecom-envelopes'],
  },
  {
    id: 'tags',
    name: 'Tags',
    tagline: 'Hang tags, price tags & swing tags — your brand\'s first impression.',
    description: 'A well-designed tag elevates the perceived value of any product. Our kraft, art paper, and seed paper tags are available in any shape, size, and finish.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__11_31_58-1787035913779.jpeg',
    alt: 'Custom hang tags and swing tags for product branding',
    category: 'Branding',
    moq: '500 units',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Standard Sizes', value: '5×9cm, 6×10cm, 7×12cm, Custom' },
      { label: 'Paper Options', value: 'Kraft 300 GSM, Art card 350 GSM, Seed paper' },
      { label: 'Shapes', value: 'Rectangle, Arch, Die-cut custom' },
      { label: 'Hole & String', value: 'Eyelet + jute / cotton string included' },
      { label: 'Finish', value: 'Matte, Gloss, Soft-touch, Uncoated' },
    ],
    materials: ['Recycled kraft board', 'FSC-certified art card', 'Jute / cotton string'],
    customizationOptions: [
      'Custom shape die-cut',
      'Double-sided print',
      'QR code / barcode',
      'Foil stamping (gold/silver)',
      'Embossing / debossing',
      'Seed paper variant (plantable)',
    ],
    useCases: ['Apparel & fashion', 'Gifts & hampers', 'Artisan food products', 'Jewellery'],
    highlights: ['Premium feel', 'Eco materials', 'Custom shapes', 'Plantable option'],
    relatedIds: ['stickers', 'clothing-labels', 'thank-you-cards'],
  },
  {
    id: 'stickers',
    name: 'Stickers',
    tagline: 'Custom die-cut & sheet stickers — small format, big brand impact.',
    description: 'From product labels to packaging seals, our stickers are printed on eco-friendly substrates with vibrant, long-lasting inks. Available in any shape and size.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__11_31_58-1787034586131.jpeg',
    alt: 'Custom die-cut stickers and product labels',
    badge: 'Popular',
    category: 'Branding',
    moq: '500 units',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Material Options', value: 'Kraft paper, White paper, BOPP, Vinyl' },
      { label: 'Finish', value: 'Matte, Gloss, Soft-touch' },
      { label: 'Shapes', value: 'Circle, Square, Rectangle, Custom die-cut' },
      { label: 'Adhesive', value: 'Permanent / Removable' },
      { label: 'Print', value: 'CMYK full colour + white ink' },
    ],
    materials: ['Recycled kraft paper', 'FSC-certified white paper', 'Water-based inks'],
    materialNote: 'Kraft and paper variants are compostable. BOPP is recyclable.',
    customizationOptions: [
      'Custom die-cut shape',
      'White ink on kraft',
      'Holographic / metallic finish',
      'Scratch-off overlay',
      'Sheet stickers (multiple per sheet)',
    ],
    useCases: ['Product labels', 'Packaging seals', 'Brand merchandise', 'Event giveaways'],
    highlights: ['Vibrant CMYK print', 'Waterproof options', 'Eco substrates', 'Any shape'],
    relatedIds: ['tags', 'thank-you-cards'],
  },
  {
    id: 'pouches',
    name: 'Pouches',
    tagline: 'Stand-up & flat pouches for products that deserve better packaging.',
    description: 'Our eco-friendly pouches combine functionality with sustainability. Available in kraft paper, compostable film, and hybrid variants for food, beauty, and retail.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__11_31_16-1787034585959.jpeg',
    alt: 'Eco-friendly stand-up pouches for product packaging',
    category: 'Packaging',
    moq: '500 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Pouch Types', value: 'Stand-up (Doypack), Flat, Gusseted' },
      { label: 'Sizes', value: '100ml to 5L capacity range' },
      { label: 'Closure', value: 'Zipper reseal, Heat seal, Spout' },
      { label: 'Barrier', value: 'Standard / High-barrier (moisture, oxygen)' },
      { label: 'Printing', value: 'Rotogravure / Digital, up to 8 colours' },
    ],
    materials: ['Kraft paper laminate', 'Compostable PLA film', 'Recycled PET (hybrid)'],
    materialNote: 'Compostable variants certified to EN 13432 / ASTM D6400.',
    customizationOptions: [
      'Custom size & capacity',
      'Window cut-out (clear or frosted)',
      'Matte or gloss lamination',
      'Hang hole',
      'Tear notch',
      'Custom zipper colour',
    ],
    useCases: ['Snacks & dry food', 'Coffee & tea', 'Supplements', 'Pet food', 'Beauty powders'],
    highlights: ['Resealable zipper', 'Moisture barrier', 'Stand-up stability', 'Compostable option'],
    relatedIds: ['ecom-boxes', 'stickers'],
  },
  {
    id: 'thank-you-cards',
    name: 'Thank You Cards',
    tagline: 'The small card that turns a customer into a repeat buyer.',
    description: 'A handwritten-feel thank you card inside every order builds loyalty. Our cards are printed on premium recycled stock and can include QR codes, discount codes, or personalised messages.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__12_10_47-1787035260188.jpeg',
    alt: 'Custom thank you cards for ecommerce packaging inserts',
    category: 'Branding',
    moq: '500 units',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Standard Sizes', value: 'A6 (105×148mm), A5, Custom' },
      { label: 'Paper', value: '300–400 GSM recycled art card' },
      { label: 'Finish', value: 'Matte, Soft-touch, Uncoated' },
      { label: 'Printing', value: 'Full colour both sides' },
      { label: 'Extras', value: 'QR code, discount code, personalisation' },
    ],
    materials: ['Recycled art card 300–400 GSM', 'Water-based inks', 'Soy-based varnish'],
    customizationOptions: [
      'Custom size & shape',
      'Personalised variable data printing',
      'QR code to review / social page',
      'Foil stamping',
      'Rounded corners',
      'Seed paper variant',
    ],
    useCases: ['D2C unboxing inserts', 'Gift packaging', 'Subscription boxes', 'Retail bags'],
    highlights: ['Boosts repeat purchases', 'Eco materials', 'QR code ready', 'Premium finish'],
    relatedIds: ['stickers', 'tags', 'ecom-envelopes'],
  },
  {
    id: 'glass-jars',
    name: 'Glass Jars',
    tagline: 'Elegant glass jars for food, beauty, and wellness brands.',
    description: 'Premium clear and frosted glass jars that communicate quality at a glance. Suitable for food, cosmetics, candles, and more. Customisable with labels, lids, and screen printing.',
    image: '/assets/images/glass_jars-1787033603820.jpeg',
    alt: 'Clear glass jars for sustainable product storage and packaging',
    badge: 'New',
    category: 'Premium',
    moq: '200 units',
    leadTime: '14–21 business days',
    specs: [
      { label: 'Capacities', value: '30ml, 50ml, 100ml, 200ml, 500ml, 1L' },
      { label: 'Glass Type', value: 'Clear, Amber, Frosted' },
      { label: 'Lid Options', value: 'Metal screw cap, Bamboo lid, Cork stopper' },
      { label: 'Neck Finish', value: 'Standard 58mm / 70mm' },
      { label: 'Certifications', value: 'Food-grade, BPA-free' },
    ],
    materials: ['Borosilicate / soda-lime glass', 'Bamboo / metal lids', 'Food-grade silicone seal'],
    materialNote: 'Glass is infinitely recyclable. Bamboo lids are sustainably sourced.',
    customizationOptions: [
      'Screen printing (logo / design)',
      'Custom label (paper or BOPP)',
      'Frosted finish',
      'Coloured glass (amber, green)',
      'Custom lid colour & material',
    ],
    useCases: ['Skincare & cosmetics', 'Candles', 'Honey & preserves', 'Spices & dry goods'],
    highlights: ['Food-grade safe', 'Infinitely recyclable', 'Premium shelf presence', 'Airtight seal'],
    relatedIds: ['stickers', 'tags'],
  },
  {
    id: 'honeycomb-paper',
    name: 'Honeycomb Paper',
    tagline: 'Eco-friendly cushioning that replaces bubble wrap — completely.',
    description: 'Honeycomb paper wrap provides superior cushioning through its hexagonal cell structure. 100% paper-based, recyclable, and biodegradable — the sustainable alternative to bubble wrap and foam.',
    image: '/assets/images/seed_paper_sheet-1787033603983.jpeg',
    alt: 'Honeycomb paper cushioning wrap for eco-friendly packaging protection',
    category: 'Protection',
    moq: '500 sheets / 1 roll',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Cell Size', value: '8mm / 12mm hexagonal cells' },
      { label: 'Sheet Sizes', value: '30×30cm, 40×40cm, 50×50cm, Roll' },
      { label: 'Thickness', value: '3mm expanded' },
      { label: 'Weight', value: '80 GSM base paper' },
      { label: 'Colour', value: 'Natural kraft brown' },
    ],
    materials: ['100% recycled kraft paper', 'Water-based adhesive'],
    materialNote: 'Fully recyclable with cardboard. No plastic. No foam.',
    customizationOptions: [
      'Custom sheet size',
      'Branded kraft wrap (print on outer)',
      'Pre-cut to product dimensions',
      'Perforated roll for easy tearing',
    ],
    useCases: ['Fragile product protection', 'Void fill', 'Gift wrapping', 'Cosmetics & glassware'],
    highlights: ['Replaces bubble wrap', '100% paper-based', 'Biodegradable', 'Strong cushioning'],
    relatedIds: ['ecom-boxes', 'ecom-envelopes'],
  },
  {
    id: 'wrapping-paper',
    name: 'Butter / Wrapping Paper',
    tagline: 'Custom & plain wrapping paper rolls for a premium unboxing moment.',
    description: 'From tissue paper to printed kraft wrapping rolls, our wrapping papers add a premium layer to every unboxing. Available in custom prints, solid colours, and natural kraft.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__11_31_58-1787035942148.jpeg',
    alt: 'Custom printed wrapping paper and tissue paper rolls',
    category: 'Packaging',
    moq: '500 sheets / 5 rolls',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Paper Types', value: 'Tissue, Kraft, MG (machine glazed)' },
      { label: 'GSM', value: '17 GSM (tissue) to 60 GSM (kraft)' },
      { label: 'Sheet Size', value: '50×70cm standard; custom available' },
      { label: 'Print', value: 'Flexo up to 4 colours; Digital full colour' },
      { label: 'Roll Width', value: '50cm, 70cm, 100cm' },
    ],
    materials: ['Recycled tissue paper', 'Kraft paper', 'Water-based inks'],
    customizationOptions: [
      'Custom print & pattern',
      'Brand logo repeat pattern',
      'Custom colour tissue',
      'Perforated sheets',
      'Custom roll length',
    ],
    useCases: ['Gift wrapping', 'Unboxing inserts', 'Retail bags lining', 'Food wrapping (MG)'],
    highlights: ['Lightweight', 'Recyclable', 'Custom patterns', 'Soft texture'],
    relatedIds: ['ecom-boxes', 'thank-you-cards'],
  },
  {
    id: 'clothing-labels',
    name: 'Cotton Clothing Labels',
    tagline: 'Woven & printed fabric labels that feel as good as they look.',
    description: 'Your clothing label is the last thing a customer sees before wearing your product. Our woven and printed cotton labels are soft, durable, and available in any size or shape.',
    image: '/assets/images/Cotton_bags-1787033569743.jpeg',
    alt: 'Woven cotton clothing labels and fabric tags for apparel',
    category: 'Apparel',
    moq: '500 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Label Types', value: 'Woven, Printed, Heat-transfer, Damask' },
      { label: 'Sizes', value: '2×4cm to 5×10cm; custom' },
      { label: 'Attachment', value: 'Fold-over, Straight cut, Mitre fold' },
      { label: 'Colours', value: 'Up to 8 thread colours (woven); CMYK (printed)' },
      { label: 'Material', value: '100% cotton / satin / recycled polyester' },
    ],
    materials: ['100% cotton fabric', 'Recycled polyester thread', 'Water-based dyes'],
    materialNote: 'OEKO-TEX certified materials available on request.',
    customizationOptions: [
      'Custom size & fold style',
      'Woven logo & text',
      'Care instruction labels',
      'QR code (printed variant)',
      'Recycled material labels',
    ],
    useCases: ['Apparel brands', 'Activewear', 'Kidswear', 'Luxury fashion'],
    highlights: ['Soft on skin', 'Durable wash-fast', 'Eco materials', 'OEKO-TEX option'],
    relatedIds: ['tags', 'tote-bags'],
  },
  {
    id: 'monocartons',
    name: 'Monocartons',
    tagline: 'Printed folding cartons for retail — where packaging becomes marketing.',
    description: 'Monocartons are the backbone of retail shelf packaging. Our precision-cut, high-quality printed cartons are available in SBS, kraft, and recycled board with a range of premium finishes.',
    image: '/assets/images/Monocartons-1787033603916.jpeg',
    alt: 'Printed monocarton folding boxes for retail product packaging',
    category: 'Packaging',
    moq: '500 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Board Options', value: 'SBS 300–400 GSM, Kraft, Recycled grey board' },
      { label: 'Box Styles', value: 'Tuck-end, Sleeve, Crash-lock, Gable' },
      { label: 'Printing', value: 'Offset CMYK + Pantone spot colours' },
      { label: 'Finish', value: 'Matte, Gloss, Soft-touch, Aqueous' },
      { label: 'Extras', value: 'Foil, Emboss, Spot UV, Window patch' },
    ],
    materials: ['SBS board (virgin / recycled)', 'Kraft board', 'Water-based inks & varnish'],
    customizationOptions: [
      'Custom structural design',
      'Foil stamping (gold, silver, holographic)',
      'Embossing / debossing',
      'Spot UV coating',
      'Window cut-out with PET film',
      'Magnetic closure',
    ],
    useCases: ['Pharmaceuticals', 'Cosmetics', 'Food & confectionery', 'Electronics accessories'],
    highlights: ['Retail-ready finish', 'Premium print quality', 'Custom structure', 'Eco board options'],
    relatedIds: ['stickers', 'tags', 'ecom-boxes'],
  },

  // ─── Apparel & Fashion ─────────────────────────────────────────────────────
  {
    id: 'paper-carry-bags',
    name: 'Paper Carry Bags',
    tagline: 'Kraft & art paper shopping bags — the eco upgrade your retail store needs.',
    description: 'Replace plastic carry bags with our premium paper alternatives. Available in kraft and art paper with twisted or flat rope handles, in any size and print.',
    image: '/assets/images/Use_AI_Image_Aug_18__2026__12_14_22-1787035485849.jpeg',
    alt: 'Kraft paper carry bags and shopping bags for retail',
    badge: 'Eco-Friendly',
    category: 'Retail',
    moq: '500 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Paper Options', value: 'Kraft 120 GSM, Art paper 150 GSM, Duplex' },
      { label: 'Sizes', value: 'Small (20×10×25cm) to XL (40×15×45cm); custom' },
      { label: 'Handle Types', value: 'Twisted kraft rope, Flat ribbon, Cotton cord' },
      { label: 'Gusset', value: '8–15cm side gusset' },
      { label: 'Printing', value: 'Flexo / Offset, up to 4 colours' },
    ],
    materials: ['Recycled kraft paper', 'FSC-certified art paper', 'Cotton / jute rope handles'],
    materialNote: 'All bags are 100% plastic-free and fully recyclable.',
    customizationOptions: [
      'Custom size & gusset',
      'Full-colour exterior print',
      'Interior print',
      'Tissue paper lining',
      'Rope colour customisation',
      'Foil / emboss on logo',
    ],
    useCases: ['Fashion retail', 'Boutiques', 'Gift shops', 'Grocery & food retail'],
    highlights: ['100% plastic-free', 'Strong handles', 'Premium look', 'Fully recyclable'],
    relatedIds: ['tote-bags', 'tags', 'stickers'],
  },
  {
    id: 'tote-bags',
    name: 'Tote Bags',
    tagline: 'Natural cotton canvas tote bags — the bag your customers keep forever.',
    description: 'Our cotton tote bags are the most reused packaging format in the world. Durable, washable, and fully customisable with screen print, embroidery, or heat transfer.',
    image: '/assets/images/tote-1787035801487.jpeg',
    alt: 'Natural cotton tote bags for eco-friendly retail packaging',
    category: 'Cotton Bags',
    moq: '200 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Canvas Weight', value: '8oz (220 GSM) / 10oz (280 GSM) / 12oz (340 GSM)' },
      { label: 'Sizes', value: '35×40cm, 38×42cm, 40×45cm, Custom' },
      { label: 'Handle Length', value: '60cm / 70cm / Custom' },
      { label: 'Colours', value: 'Natural, Black, Navy, Custom dyed' },
      { label: 'Print Methods', value: 'Screen print, Embroidery, Heat transfer' },
    ],
    materials: ['100% natural cotton canvas', 'Organic cotton option available', 'Water-based screen inks'],
    materialNote: 'Organic cotton certified to GOTS standard available on request.',
    customizationOptions: [
      'Custom size & handle length',
      'Screen print (up to 6 colours)',
      'Embroidery',
      'Inside pocket',
      'Zipper closure',
      'Custom dyed canvas',
    ],
    useCases: ['Retail shopping bags', 'Brand merchandise', 'Event giveaways', 'Corporate gifting'],
    highlights: ['Reusable 500+ times', 'Machine washable', 'GOTS organic option', 'Premium canvas'],
    relatedIds: ['paper-carry-bags', 'drawstring-bags', 'clothing-labels'],
  },
  {
    id: 'running-bags',
    name: 'Running Bags',
    tagline: 'Lightweight cotton running pouches for active lifestyles.',
    description: 'Compact, lightweight cotton pouches designed for runners and active users. Perfect as brand merchandise, event giveaways, or retail accessories.',
    image: '/assets/images/Cotton_bags-1787033569743.jpeg',
    alt: 'Lightweight cotton running pouches and sport bags',
    category: 'Cotton Bags',
    moq: '200 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Canvas Weight', value: '6oz (160 GSM) / 8oz (220 GSM)' },
      { label: 'Sizes', value: '25×30cm, 30×35cm, Custom' },
      { label: 'Closure', value: 'Drawstring / Zipper' },
      { label: 'Colours', value: 'Natural, Black, Custom' },
      { label: 'Print', value: 'Screen print, Heat transfer' },
    ],
    materials: ['100% cotton canvas', 'Cotton drawstring cord', 'Water-based inks'],
    customizationOptions: [
      'Custom size',
      'Screen print logo',
      'Custom cord colour',
      'Zipper upgrade',
      'Inner lining',
    ],
    useCases: ['Sports events', 'Gym merchandise', 'Brand giveaways', 'Retail accessories'],
    highlights: ['Ultra-lightweight', 'Compact fold', 'Durable cotton', 'Custom print'],
    relatedIds: ['tote-bags', 'drawstring-bags'],
  },
  {
    id: 'saree-bags',
    name: 'Saree Bags',
    tagline: 'Traditional saree storage bags — functional, beautiful, and branded.',
    description: 'Protect and present sarees in style with our custom cotton saree bags. Available in natural cotton, muslin, and premium dupion fabric with embroidery or print options.',
    image: '/assets/images/Cotton_bags-1787033569743.jpeg',
    alt: 'Cotton saree storage bags for traditional garment packaging',
    category: 'Cotton Bags',
    moq: '200 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Fabric Options', value: 'Cotton muslin, Canvas, Dupion silk' },
      { label: 'Size', value: '40×50cm, 45×55cm, Custom' },
      { label: 'Closure', value: 'Drawstring / Button / Zipper' },
      { label: 'Colours', value: 'Natural, Ivory, Custom dyed' },
      { label: 'Decoration', value: 'Embroidery, Screen print, Block print' },
    ],
    materials: ['100% cotton muslin', 'Natural cotton drawstring', 'Eco-friendly dyes'],
    customizationOptions: [
      'Custom size',
      'Embroidery (logo / motif)',
      'Block print pattern',
      'Custom fabric colour',
      'Ribbon tie closure',
    ],
    useCases: ['Saree retail', 'Wedding gifting', 'Boutique packaging', 'Trousseau packaging'],
    highlights: ['Breathable fabric', 'Protects from dust', 'Elegant presentation', 'Reusable'],
    relatedIds: ['tote-bags', 'drawstring-bags', 'tags'],
  },
  {
    id: 'drawstring-bags',
    name: 'Drawstring Bags',
    tagline: 'Versatile drawstring cotton bags for every brand and occasion.',
    description: 'Simple, functional, and endlessly reusable — our drawstring bags are a staple for retail, events, and gifting. Available in cotton, jute, and organza variants.',
    image: '/assets/images/Cotton_bags-1787033569743.jpeg',
    alt: 'Cotton drawstring bags for retail and event packaging',
    category: 'Cotton Bags',
    moq: '200 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Material Options', value: 'Cotton canvas, Jute, Organza' },
      { label: 'Sizes', value: '15×20cm to 40×50cm; custom' },
      { label: 'Cord', value: 'Cotton / Jute / Satin ribbon' },
      { label: 'Colours', value: 'Natural, Black, Custom dyed' },
      { label: 'Print', value: 'Screen print, Embroidery' },
    ],
    materials: ['100% cotton / jute', 'Natural cord', 'Water-based inks'],
    customizationOptions: [
      'Custom size',
      'Screen print logo',
      'Embroidery',
      'Custom cord colour & material',
      'Lined interior',
    ],
    useCases: ['Jewellery packaging', 'Gift bags', 'Event giveaways', 'Shoe bags'],
    highlights: ['Lightweight', 'Reusable', 'Eco materials', 'Versatile sizes'],
    relatedIds: ['tote-bags', 'running-bags', 'tags'],
  },

  // ─── Conference & Event Kits ───────────────────────────────────────────────
  {
    id: 'kraft-folders',
    name: 'Kraft Paper Folders',
    tagline: 'Elegant conference document folders — no plastic, all class.',
    description: 'Make every delegate feel valued with our premium kraft paper folders. Designed for conferences, seminars, and corporate events, these folders hold documents, notepads, and pens in style.',
    image: '/assets/images/folders-1787042123432.jpeg',
    alt: 'Kraft paper conference document folders for events',
    badge: 'No Plastic',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Size', value: 'A4 (fits A4 documents & notepads)' },
      { label: 'Board', value: '300–350 GSM recycled kraft board' },
      { label: 'Pockets', value: '2 interior pockets + business card slot' },
      { label: 'Closure', value: 'Elastic band / Ribbon tie' },
      { label: 'Printing', value: 'Offset / Digital, full colour' },
    ],
    materials: ['Recycled kraft board 300–350 GSM', 'Water-based inks', 'Cotton elastic / ribbon'],
    materialNote: '100% plastic-free. Fully recyclable after use.',
    customizationOptions: [
      'Custom logo & event branding',
      'Interior print',
      'Custom pocket configuration',
      'Ribbon colour',
      'Debossing / foil stamp',
      'Pen loop addition',
    ],
    useCases: ['Corporate conferences', 'Seminars & workshops', 'Award ceremonies', 'Trade shows'],
    highlights: ['100% plastic-free', 'Premium kraft feel', 'Holds A4 documents', 'Fully branded'],
    relatedIds: ['cotton-lanyards', 'kraft-id-cards', 'seed-notepad'],
  },
  {
    id: 'cotton-lanyards',
    name: 'Cotton Lanyards',
    tagline: 'Soft cotton ID card lanyards — comfortable, branded, plastic-free.',
    description: 'Replace synthetic lanyards with our 100% cotton alternatives. Soft on the neck, durable through long event days, and available with custom woven or printed branding.',
    image: '/assets/images/cotton_lanyard-1787042123398.jpeg',
    alt: 'Cotton lanyards for ID cards at conferences and events',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Width', value: '10mm / 15mm / 20mm' },
      { label: 'Length', value: '90cm standard (adjustable)' },
      { label: 'Attachment', value: 'Metal swivel hook / Safety breakaway' },
      { label: 'Branding', value: 'Woven logo / Screen print / Sublimation' },
      { label: 'Colours', value: 'Natural, Black, Custom dyed' },
    ],
    materials: ['100% cotton', 'Metal swivel clip', 'Eco-friendly dyes'],
    materialNote: 'No polyester. No nylon. 100% natural cotton.',
    customizationOptions: [
      'Custom width & length',
      'Woven logo (most durable)',
      'Sublimation full-colour print',
      'Safety breakaway clip',
      'Custom dyed colour',
      'Retractable badge reel add-on',
    ],
    useCases: ['Conferences & summits', 'Corporate events', 'Trade shows', 'School & university events'],
    highlights: ['Soft on neck', 'Durable all-day wear', 'No synthetic fibres', 'Custom woven branding'],
    relatedIds: ['kraft-id-cards', 'seed-id-cards', 'kraft-folders'],
  },
  {
    id: 'kraft-id-cards',
    name: 'Kraft Paper ID Cards',
    tagline: 'Recycled kraft paper ID holders — functional, sustainable, on-brand.',
    description: 'Our kraft paper ID card holders replace plastic badge holders entirely. Sturdy enough for multi-day events, printed with your event branding, and 100% recyclable after use.',
    image: '/assets/images/id_card_kraft-1787042635382.jpeg',
    alt: 'Kraft paper ID card holders for conferences and events',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Size', value: 'CR80 (85×54mm) / A6 (105×74mm)' },
      { label: 'Board', value: '300 GSM recycled kraft' },
      { label: 'Window', value: 'Clear PET window / Open slot' },
      { label: 'Hole', value: 'Reinforced eyelet for lanyard' },
      { label: 'Printing', value: 'Full colour both sides' },
    ],
    materials: ['Recycled kraft board 300 GSM', 'Water-based inks', 'Reinforced metal eyelet'],
    materialNote: 'PET window is recyclable. Kraft body is compostable.',
    customizationOptions: [
      'Custom size',
      'Event branding print',
      'Attendee name variable printing',
      'QR code per attendee',
      'No-window open slot variant',
    ],
    useCases: ['Conference badges', 'Event passes', 'Workshop IDs', 'Trade show badges'],
    highlights: ['No plastic body', 'Sturdy kraft board', 'Full-colour print', 'Recyclable'],
    relatedIds: ['cotton-lanyards', 'seed-id-cards', 'kraft-folders'],
  },
  {
    id: 'seed-id-cards',
    name: 'Seed Paper ID Cards',
    tagline: 'Plant-after-use seed paper IDs — the badge that grows into wildflowers.',
    description: 'The most memorable conference badge your attendees will ever receive. Made from handmade seed paper embedded with wildflower or herb seeds — plant it after the event and watch it grow.',
    image: '/assets/images/seed_paper_id-1787042671153.jpeg',
    alt: 'Seed paper ID cards that can be planted after use',
    badge: 'Plantable',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Size', value: 'CR80 (85×54mm) / A6 / Custom' },
      { label: 'Paper', value: 'Handmade seed paper 200–250 GSM' },
      { label: 'Seeds', value: 'Wildflower mix / Herb mix / Custom' },
      { label: 'Hole', value: 'Reinforced eyelet' },
      { label: 'Printing', value: 'Soy-based ink, full colour' },
    ],
    materials: ['Handmade recycled paper', 'Embedded wildflower / herb seeds', 'Soy-based inks'],
    materialNote: 'Entire card is plantable — no separation needed. Grows in 2–3 weeks.',
    customizationOptions: [
      'Custom seed mix (wildflower, herbs, vegetables)',
      'Custom size & shape',
      'Event branding print',
      'Attendee name variable printing',
      'Planting instruction card',
    ],
    useCases: ['Sustainability conferences', 'Green events', 'Corporate CSR events', 'Nature & wellness summits'],
    highlights: ['100% plantable', 'Zero waste after event', 'Wildflower or herb seeds', 'Memorable keepsake'],
    relatedIds: ['cotton-lanyards', 'kraft-id-cards', 'seed-notepad'],
  },
  {
    id: 'seed-notepad',
    name: 'Seed Paper Notepad',
    tagline: 'Conference notepads you can plant — take notes, then grow wildflowers.',
    description: 'Our seed paper notepads let attendees take notes during the event and plant the pages afterwards. Each sheet is embedded with wildflower seeds and printed with soy-based inks.',
    image: '/assets/images/notepads-1787043144020.jpeg',
    alt: 'Seed paper notepads for sustainable conferences and events',
    badge: 'Eco',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '10–14 business days',
    specs: [
      { label: 'Size', value: 'A5 (148×210mm) / A6 / Custom' },
      { label: 'Pages', value: '20 / 40 / 60 pages per pad' },
      { label: 'Paper', value: 'Seed paper 120 GSM per sheet' },
      { label: 'Cover', value: 'Kraft board 300 GSM' },
      { label: 'Binding', value: 'Glue-bound / Spiral' },
    ],
    materials: ['Handmade seed paper sheets', 'Recycled kraft cover board', 'Soy-based inks'],
    materialNote: 'Every page is plantable. Cover is 100% recyclable.',
    customizationOptions: [
      'Custom cover print & branding',
      'Custom page count',
      'Seed mix selection',
      'Spiral or glue binding',
      'Custom size',
      'Planting guide insert',
    ],
    useCases: ['Conferences & seminars', 'Corporate gifting', 'Sustainability events', 'School workshops'],
    highlights: ['Every page plantable', 'Soy-ink printed', 'Kraft cover', 'Zero waste'],
    relatedIds: ['seed-pens', 'seed-id-cards', 'kraft-folders'],
  },
  {
    id: 'seed-pens',
    name: 'Seed Pens',
    tagline: 'Write, use, then plant the pen — it grows into herbs or wildflowers.',
    description: 'Our seed pens are made from recycled newspaper rolled into a pen body, with a seed capsule at the end. When the ink runs out, plant the pen and watch it sprout.',
    image: '/assets/images/seed_pens-1787043144018.jpeg',
    alt: 'Seed pens made from recycled newspaper that can be planted',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '7–10 business days',
    specs: [
      { label: 'Body Material', value: 'Rolled recycled newspaper' },
      { label: 'Ink', value: 'Black / Blue refillable ink' },
      { label: 'Seed Capsule', value: 'Tip end with seed pod' },
      { label: 'Seeds', value: 'Basil, Coriander, Tomato, Wildflower' },
      { label: 'Length', value: '14cm standard' },
    ],
    materials: ['Recycled newspaper', 'Non-toxic ink', 'Seed capsule (paper + seeds)'],
    materialNote: 'Entire pen is plantable. No plastic components.',
    customizationOptions: [
      'Custom seed variety',
      'Branded paper wrap (logo print)',
      'Custom ink colour',
      'Bulk packaging (box of 10 / 50)',
      'Planting instruction sleeve',
    ],
    useCases: ['Conference giveaways', 'Corporate gifting', 'School stationery', 'Eco brand merchandise'],
    highlights: ['100% plantable', 'No plastic', 'Recycled newspaper body', 'Unique conversation starter'],
    relatedIds: ['seed-notepad', 'seed-id-cards', 'kraft-folders'],
  },
  {
    id: 'kraft-certificates',
    name: 'Kraft Paper Certificates',
    tagline: 'Premium kraft paper certificates — recognition that feels as good as it looks.',
    description: 'Award certificates printed on premium recycled kraft board. The natural texture and earthy tone of kraft paper gives certificates a premium, artisanal feel that stands out from standard white paper.',
    image: '/assets/images/certificate-1787043194669.jpeg',
    alt: 'Kraft paper certificates for conferences and award ceremonies',
    category: 'Conference & Events',
    moq: '100 units',
    leadTime: '5–7 business days',
    specs: [
      { label: 'Size', value: 'A4 (210×297mm) / A3 / Custom' },
      { label: 'Board', value: '300–350 GSM recycled kraft' },
      { label: 'Printing', value: 'Offset / Digital, full colour' },
      { label: 'Finish', value: 'Uncoated natural / Matte laminate' },
      { label: 'Variable Data', value: 'Name, date, achievement per certificate' },
    ],
    materials: ['Recycled kraft board 300–350 GSM', 'Soy-based inks', 'Water-based varnish'],
    materialNote: 'Natural uncoated kraft is fully recyclable and compostable.',
    customizationOptions: [
      'Custom design & branding',
      'Variable data printing (names, dates)',
      'Foil stamping (gold / silver)',
      'Embossing / debossing',
      'Envelope included',
      'Tube packaging option',
    ],
    useCases: ['Conference awards', 'Training completions', 'Academic certificates', 'Corporate recognition'],
    highlights: ['Premium kraft texture', 'Variable name printing', 'Foil stamp option', 'Eco materials'],
    relatedIds: ['kraft-folders', 'kraft-id-cards', 'cotton-lanyards'],
  },
  {
    id: 'event-kits',
    name: 'Complete Event Kits',
    tagline: 'All-in-one no-plastic event bundles — everything your delegates need, nothing the planet doesn\'t.',
    description: 'Our complete event kits bundle all conference essentials into one sustainable package. Choose from our standard kit or build a fully custom bundle tailored to your event theme and brand.',
    image: '/assets/images/collage-1787037856709.jpeg',
    alt: 'Complete sustainable event kit bundle for conferences',
    badge: 'Bundle',
    category: 'Conference & Events',
    moq: '50 kits',
    leadTime: '14–21 business days',
    specs: [
      { label: 'Standard Kit Includes', value: 'Folder, Lanyard, ID card, Notepad, Pen' },
      { label: 'Packaging', value: 'Kraft paper bag or cotton tote' },
      { label: 'Customisation', value: 'Full branding on all items' },
      { label: 'MOQ', value: '50 kits (custom) / 100 kits (standard)' },
      { label: 'Lead Time', value: '14–21 business days' },
    ],
    materials: ['All items: kraft paper, cotton, seed paper', 'Zero virgin plastic across entire kit'],
    materialNote: 'Every item in the kit is either recyclable, compostable, or plantable.',
    customizationOptions: [
      'Choose items to include in kit',
      'Full branding on all items',
      'Custom kit bag (kraft or cotton tote)',
      'Seed paper or kraft variants',
      'Welcome card insert',
      'Custom kit packaging box',
    ],
    useCases: ['Corporate conferences', 'Sustainability summits', 'Trade shows', 'Award ceremonies'],
    highlights: ['Zero plastic guarantee', 'All items branded', 'Single vendor convenience', 'Bulk pricing'],
    relatedIds: ['kraft-folders', 'cotton-lanyards', 'seed-notepad'],
  },
];

export function getProductById(id: string): ProductData | undefined {
  return allProducts.find((p) => p.id === id);
}

export function getRelatedProducts(ids: string[]): ProductData[] {
  return ids.map((id) => allProducts.find((p) => p.id === id)).filter(Boolean) as ProductData[];
}

export { allProducts };
